import React, { useState, useEffect, useRef } from 'react';
import { Shield, Lock, Eye, Database, Cookie, FileText, Mail, MapPin } from 'lucide-react';

export default function PrivacyPolicy() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Hero Section */}
      <section className="border-b border-zinc-200" data-animate="hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`text-center transition-all duration-1000 ease-out ${
            visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-blue-600 text-white text-xs font-medium tracking-wider mb-8 rounded">
              <Shield size={14} />
              PRIVACY POLICY
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-lg text-zinc-600 mb-4">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
              Digital Trent is committed to protecting your privacy and ensuring the security of your personal data in accordance with UK GDPR and the Data Protection Act 2018.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="border-b border-zinc-200" data-animate="intro">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Introduction</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>
                Digital Trent ("we", "our", or "us") operates the Digital Trent platform (the "Service"), an AI-powered marketing and CRM platform. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Service.
              </p>
              <p>
                We are committed to protecting your privacy and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. This policy describes your data protection rights, including the right to object to some of the processing which we carry out.
              </p>
              <p className="font-medium text-zinc-900">
                Digital Trent is the data controller responsible for your personal data. If you have any questions about this Privacy Policy or our data practices, please contact us using the details provided at the end of this policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data We Collect */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="collect">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('collect') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Database size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Information We Collect</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-zinc-900">Information You Provide to Us</h3>
                <ul className="space-y-2 text-zinc-600 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Account Information:</strong> Name, email address, business name, phone number, and password when you create an account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Business Information:</strong> Industry, target audience, marketing goals, product/service descriptions, and positioning statements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Payment Information:</strong> Billing address and payment details (processed securely through Stripe)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Communications:</strong> Information you provide when contacting our support team or through our contact forms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Content:</strong> Marketing content, customer data, and campaign information you upload or create within the platform</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-zinc-900">Information We Collect Automatically</h3>
                <ul className="space-y-2 text-zinc-600 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform, and interaction patterns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Cookies and Tracking:</strong> Information collected through cookies and similar technologies (see Cookies section below)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Performance Data:</strong> Campaign performance metrics, engagement rates, and analytics data</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Use Data */}
      <section className="border-b border-zinc-200" data-animate="use">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('use') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Eye size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">How We Use Your Information</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>Under UK GDPR, we must have a lawful basis for processing your personal data. We process your information for the following purposes:</p>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg space-y-3">
                <div>
                  <p className="font-medium text-zinc-900 mb-1">Contract Performance (GDPR Article 6(1)(b))</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Providing and maintaining the Service</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Generating AI-powered marketing strategies and content</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Processing payments and managing subscriptions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Providing customer support</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium text-zinc-900 mb-1">Legitimate Interests (GDPR Article 6(1)(f))</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Improving and optimising our Service and AI algorithms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Detecting and preventing fraud and security threats</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Analysing usage patterns to enhance user experience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Sending service updates and important notices</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium text-zinc-900 mb-1">Consent (GDPR Article 6(1)(a))</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Sending marketing communications (you can opt out at any time)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Using cookies for analytics and advertising (where required)</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium text-zinc-900 mb-1">Legal Obligation (GDPR Article 6(1)(c))</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Complying with legal requirements and regulatory obligations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Responding to lawful requests from authorities</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Sharing */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="sharing">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('sharing') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">How We Share Your Information</h2>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>We do not sell your personal data. We may share your information with the following third parties:</p>

              <div className="space-y-4">
                <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Service Providers</h3>
                  <p className="mb-2">We work with trusted service providers who assist in operating our platform:</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Anthropic (Claude AI):</strong> For AI-powered content generation and strategy development</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Stripe:</strong> For secure payment processing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Resend:</strong> For transactional email delivery</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Railway & Vercel:</strong> For cloud hosting infrastructure</span>
                    </li>
                  </ul>
                  <p className="mt-2 text-sm">All service providers are required to protect your data and use it only for the purposes we specify.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Legal Requirements</h3>
                  <p>We may disclose your information if required by law, court order, or to protect our rights, property, or safety, or that of our users or the public.</p>
                </div>

                <div className="bg-white border border-zinc-200 p-6 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Business Transfers</h3>
                  <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Retention */}
      <section className="border-b border-zinc-200" data-animate="retention">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('retention') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Lock size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Data Retention and Security</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-3 text-zinc-900">How Long We Keep Your Data</h3>
                <div className="space-y-2 text-zinc-600 leading-relaxed">
                  <p>We retain your personal data only for as long as necessary to fulfil the purposes outlined in this Privacy Policy:</p>
                  <ul className="space-y-2 ml-4 mt-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Active Accounts:</strong> We retain your data while your account is active and you use our Service</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Closed Accounts:</strong> After account closure, we retain data for 90 days for recovery purposes, then permanently delete it</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Financial Records:</strong> Billing and payment information is retained for 7 years to comply with UK tax and accounting regulations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span><strong>Marketing Communications:</strong> Until you unsubscribe or request deletion</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-zinc-900">Security Measures</h3>
                <div className="space-y-2 text-zinc-600 leading-relaxed">
                  <p>We implement industry-standard security measures to protect your data:</p>
                  <ul className="space-y-2 ml-4 mt-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Encryption of data in transit (HTTPS/TLS) and at rest</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Secure password hashing using bcrypt</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Regular security audits and vulnerability assessments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Access controls and authentication mechanisms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Monitoring for unauthorised access and suspicious activity</span>
                    </li>
                  </ul>
                  <p className="mt-3 text-sm">While we strive to protect your data, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="rights">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('rights') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <FileText size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Your Data Protection Rights</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>Under UK GDPR, you have the following rights regarding your personal data:</p>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Access</h3>
                  <p className="text-sm">Request a copy of the personal data we hold about you</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Rectification</h3>
                  <p className="text-sm">Request correction of inaccurate or incomplete data</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Erasure</h3>
                  <p className="text-sm">Request deletion of your personal data ("right to be forgotten")</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Restriction</h3>
                  <p className="text-sm">Request restriction of processing in certain circumstances</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Data Portability</h3>
                  <p className="text-sm">Receive your data in a structured, machine-readable format</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Object</h3>
                  <p className="text-sm">Object to processing based on legitimate interests or for direct marketing</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Withdraw Consent</h3>
                  <p className="text-sm">Withdraw consent for processing where consent is the legal basis</p>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Right to Complain</h3>
                  <p className="text-sm">Lodge a complaint with the ICO if you believe your rights have been violated</p>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-600 p-6 rounded-lg mt-6">
                <p className="font-medium text-zinc-900 mb-2">How to Exercise Your Rights</p>
                <p>To exercise any of these rights, please contact us at <a href="mailto:privacy@digitaltrent.co.uk" className="text-blue-600 hover:underline">privacy@digitaltrent.co.uk</a>. We will respond to your request within one month as required by UK GDPR.</p>
                <p className="mt-2 text-sm">You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a> or by calling 0303 123 1113.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookies */}
      <section className="border-b border-zinc-200" data-animate="cookies">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('cookies') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Cookie size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Cookies and Tracking Technologies</h2>
            </div>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>We use cookies and similar tracking technologies to improve your experience on our platform. Under the Privacy and Electronic Communications Regulations (PECR), we require your consent for non-essential cookies.</p>

              <div className="space-y-4 mt-6">
                <div className="bg-blue-50 border border-blue-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Essential Cookies (No consent required)</h3>
                  <p>These cookies are necessary for the platform to function and cannot be disabled:</p>
                  <ul className="space-y-1 ml-4 mt-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Authentication and session management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Security and fraud prevention</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Load balancing and performance</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Analytics Cookies (Consent required)</h3>
                  <p>Help us understand how users interact with the platform:</p>
                  <ul className="space-y-1 ml-4 mt-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Usage patterns and feature adoption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Performance monitoring and error tracking</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white border border-zinc-200 p-5 rounded-lg">
                  <h3 className="font-bold text-zinc-900 mb-2">Functional Cookies (Consent required)</h3>
                  <p>Enable enhanced functionality and personalisation:</p>
                  <ul className="space-y-1 ml-4 mt-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Remembering your preferences and settings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Personalised content recommendations</span>
                    </li>
                  </ul>
                </div>
              </div>

              <p className="mt-4">You can manage your cookie preferences at any time through your browser settings or our cookie preference centre. Note that disabling cookies may affect platform functionality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* International Transfers */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="transfers">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('transfers') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">International Data Transfers</h2>

            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>Some of our service providers are located outside the United Kingdom. When we transfer your data internationally, we ensure appropriate safeguards are in place:</p>

              <ul className="space-y-2 ml-4 mt-3">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Transfers to countries with adequacy decisions recognised by the UK</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Standard Contractual Clauses (SCCs) approved by the UK authorities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Binding Corporate Rules for international organisations</span>
                </li>
              </ul>

              <div className="bg-white border border-zinc-200 p-6 rounded-lg mt-4">
                <p className="font-medium text-zinc-900 mb-2">Our Key Service Providers:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Anthropic (USA):</strong> AI processing - protected by Standard Contractual Clauses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Stripe (USA):</strong> Payment processing - certified under recognised frameworks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span><strong>Vercel & Railway (USA):</strong> Hosting infrastructure - protected by appropriate safeguards</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Children's Privacy */}
      <section className="border-b border-zinc-200" data-animate="children">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('children') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Children's Privacy</h2>
            <div className="bg-white border-2 border-blue-600 p-6 rounded-lg">
              <p className="text-zinc-600 leading-relaxed">
                Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If you believe we have inadvertently collected information from a child, please contact us immediately at <a href="mailto:privacy@digitaltrent.co.uk" className="text-blue-600 hover:underline">privacy@digitaltrent.co.uk</a> so we can delete it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Changes to Policy */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="changes">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('changes') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Changes to This Privacy Policy</h2>
            <div className="space-y-4 text-zinc-600 leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Update the "Last updated" date at the top of this policy</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Notify you via email or through a prominent notice on our platform</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Where required by law, obtain your consent for significant changes</span>
                </li>
              </ul>
              <p className="mt-4">
                We encourage you to review this Privacy Policy periodically. Your continued use of the Service after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="border-b border-zinc-200" data-animate="contact">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded">
                <Mail size={20} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Contact Us</h2>
            </div>

            <div className="space-y-6">
              <p className="text-zinc-600 leading-relaxed">
                If you have any questions about this Privacy Policy, wish to exercise your data protection rights, or have concerns about how we handle your personal data, please contact us:
              </p>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg space-y-4">
                <div>
                  <p className="font-bold text-zinc-900 mb-2">Data Controller:</p>
                  <p className="text-zinc-600">Digital Trent</p>
                </div>

                <div className="flex items-start gap-3">
                  <Mail size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-zinc-900">Email:</p>
                    <a href="mailto:privacy@digitaltrent.co.uk" className="text-blue-600 hover:underline">privacy@digitaltrent.co.uk</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-zinc-900">Location:</p>
                    <p className="text-zinc-600">Nottingham, United Kingdom</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-600 p-6 rounded-lg">
                <p className="font-bold text-zinc-900 mb-2">Information Commissioner's Office (ICO)</p>
                <p className="text-zinc-600 mb-2">If you are not satisfied with our response, you have the right to lodge a complaint with the UK's supervisory authority:</p>
                <p className="text-zinc-600">
                  Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a><br />
                  Telephone: 0303 123 1113<br />
                  Address: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
